import { memo, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import { AvatarGroup } from "@/components/common/AvatarGroup";
import { useStudyingUsers, type StudyingUser } from "@/hooks/use-studying-users";
import { useIsMobile } from "@/hooks/use-mobile";
import { getCategoryMeta } from "@/lib/categories";
import { typeTitleClass } from "@/lib/typography";
import { cn, slugify } from "@/lib/utils";
import { formattersFor, useLanguage } from "@/i18n";

interface TopicGroup {
  topicTitle: string;
  sessionSlug: string | null;
  users: StudyingUser[];
  count: number;
  /** The session's own cover. Null means this room has no picture. */
  bannerUrl: string | null;
  Icon: ReturnType<typeof getCategoryMeta>["icon"];
}

/** Rooms shown before the rest fold behind the header control. */
const MAX_VISIBLE_DESKTOP = 4;
const MAX_VISIBLE_PHONE = 2;

/**
 * Live presence on Home: the rooms other learners are practising in right now.
 *
 * ## Only the session's own cover is a picture
 *
 * A room is shown as its topic's banner when the session has one. When it does
 * not, the card is a *typographic* cover — the topic in serif on paper with its
 * category glyph — rather than a stock photo borrowed from the category. That
 * substitution was the bug: a category's generic art is not this topic, so
 * "Your Daily Routine" ended up illustrated with a coffee cup and "Ordering
 * coffee" with a laptop. Inventing a picture is worse than having none.
 *
 * ## Small on purpose
 *
 * The band is a shortcut, not a gallery: 80px covers on a phone and 96px on a
 * desktop, two rooms before the fold on a phone and four on a desktop. A lone
 * room takes half the row, not three quarters.
 *
 * Otherwise it stays quiet: no card wrapping the cards, no eyebrow above the
 * heading, no live dot, no coloured rail.
 */
export const HomeStudyingNow = memo(({ className }: { className?: string }) => {
  const { studyingUsers } = useStudyingUsers();
  const { t } = useTranslation();
  const language = useLanguage();
  const format = language?.format ?? formattersFor("en");
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);

  const topicGroups = useMemo<TopicGroup[]>(() => {
    const groups = new Map<string, TopicGroup>();

    for (const user of studyingUsers) {
      const title = user.topicTitle || t("home.discover.unknownTopic", "Unknown topic");
      const slug = user.sessionSlug ?? null;
      const key = slug || title;
      const { icon } = getCategoryMeta(user.category || "General");

      const existing = groups.get(key);
      if (existing) {
        existing.users.push(user);
        existing.count += 1;
        if (!existing.sessionSlug && slug) {
          existing.sessionSlug = slug;
        }
        if (!existing.bannerUrl && user.bannerUrl) {
          existing.bannerUrl = user.bannerUrl;
        }
      } else {
        groups.set(key, {
          topicTitle: title,
          sessionSlug: slug,
          users: [user],
          count: 1,
          bannerUrl: user.bannerUrl ?? null,
          Icon: icon,
        });
      }
    }

    return Array.from(groups.values()).sort((a, b) => b.count - a.count);
  }, [studyingUsers, t]);

  if (topicGroups.length === 0) {
    return null;
  }

  const unknown = t("home.discover.unknownTopic", "Unknown topic");
  const singleRoom = topicGroups.length === 1;
  const maxVisible = isMobile ? MAX_VISIBLE_PHONE : MAX_VISIBLE_DESKTOP;
  const hiddenRooms = Math.max(0, topicGroups.length - maxVisible);
  const visible = expanded ? topicGroups : topicGroups.slice(0, maxVisible);

  const labelFor = (group: TopicGroup) =>
    group.topicTitle === unknown
      ? t("home.studyingNow.anonymousTopic", "Studying")
      : group.topicTitle;

  const coverHeight = singleRoom ? "h-24 sm:h-28" : "h-20 sm:h-24";

  return (
    <section
      aria-labelledby="studying-now-heading"
      className={cn("space-y-2.5", className)}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="studying-now-heading" className={typeTitleClass}>
          {t("home.studyingNow.label", "Studying now")}
        </h2>
        {hiddenRooms > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((open) => !open)}
            aria-expanded={expanded}
            className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-stone-500 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background dark:text-stone-400"
          >
            {expanded
              ? t("home.studyingNow.showLess", "Show less")
              : t("home.studyingNow.moreRooms", {
                  count: hiddenRooms,
                  defaultValue: hiddenRooms === 1 ? "+{{count}} room" : "+{{count}} rooms",
                })}
          </button>
        )}
      </div>

      <ul className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
        {visible.map((group) => {
          const label = labelFor(group);
          const slug = group.sessionSlug || slugify(group.topicTitle);
          const canOpen = Boolean(slug) && group.topicTitle !== unknown;
          const hasArt = Boolean(group.bannerUrl);
          const learners = t("home.studyingNow.learnersShort", {
            count: group.count,
            total: format.number(group.count),
            defaultValue: group.count === 1 ? "{{total}} learner" : "{{total}} learners",
          });
          const Icon = group.Icon;

          return (
            <li
              key={group.sessionSlug || group.topicTitle}
              className={cn(singleRoom && "col-span-2 sm:col-span-2 lg:col-span-2")}
            >
              {canOpen ? (
                <Link
                  to={`/session/${slug}`}
                  aria-label={`${t("home.studyingNow.join", "Join room")}: ${label}`}
                  className={cn(
                    "group relative block overflow-hidden rounded-xl border border-stone-200/80",
                    "transition-colors duration-300 focus-visible:outline-none",
                    "hover:border-primary/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                    "dark:border-stone-800",
                  )}
                >
                {hasArt ? (
                  <>
                    <img
                      src={group.bannerUrl as string}
                      alt=""
                      loading="lazy"
                      className={cn(
                        "w-full object-cover",
                        "transition-transform duration-500 ease-out group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100",
                        coverHeight,
                      )}
                    />
                    {/* Tinted from the ink, never pure black, so white type
                        keeps a 4.5:1 floor over any cover. */}
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 bg-gradient-to-t from-stone-950/85 via-stone-900/35 to-stone-900/5"
                    />
                  </>
                ) : (
                  /* No cover: paper and the topic's category glyph. Never a
                     stock photo standing in for a subject it does not show. */
                  <span
                    aria-hidden="true"
                    className={cn(
                      "block w-full bg-[linear-gradient(140deg,hsl(40_38%_97%)_0%,hsl(214_38%_96.5%)_100%)]",
                      "dark:bg-[linear-gradient(140deg,hsl(30_12%_14%)_0%,hsl(220_16%_15%)_100%)]",
                      coverHeight,
                    )}
                  />
                )}

                {!hasArt && (
                  <Icon
                    aria-hidden="true"
                    className="absolute right-2 top-2 h-4 w-4 text-stone-500/70 dark:text-stone-400/70"
                  />
                )}

                <span className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-2.5">
                  <span className="min-w-0">
                    {/* A topic name is the one thing that must be readable:
                        two lines on a phone, one clipped line once there is
                        room. */}
                    <span
                      className={cn(
                        "block font-display font-semibold leading-tight line-clamp-2 sm:truncate",
                        hasArt ? "text-white" : "text-stone-900 dark:text-stone-50",
                        singleRoom ? "text-base sm:text-lg" : "text-[13px] sm:text-sm",
                      )}
                    >
                      {label}
                    </span>
                    <span className="mt-1 flex items-center gap-1.5">
                      {/* The room is live right now — one pulsing dot per
                          card, not a badge in the corner. The ring is the
                          animation; the dot itself holds still for anyone who
                          asked for reduced motion. */}
                      <span
                        className="relative flex h-2 w-2 shrink-0"
                        aria-hidden="true"
                      >
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70 motion-reduce:hidden" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_0_2px_rgba(16,185,129,0.15)]" />
                      </span>
                      <AvatarGroup
                        activeUsers={group.users}
                        maxVisible={2}
                        size="xs"
                        className="hidden shrink-0 sm:flex"
                      />
                      <span
                        className={cn(
                          "truncate text-[11px] font-medium",
                          hasArt
                            ? "text-white/85"
                            : "text-stone-500 dark:text-stone-400",
                        )}
                      >
                        {learners}
                      </span>
                    </span>
                  </span>
                  <ArrowRight
                    className={cn(
                      "h-4 w-4 shrink-0 transition-transform duration-300 group-hover:translate-x-0.5 motion-reduce:transition-none",
                      hasArt ? "text-white/85" : "text-stone-400",
                    )}
                    aria-hidden="true"
                  />
                </span>
                </Link>
              ) : (
                <div
                  className={cn(
                    "group relative block overflow-hidden rounded-xl border border-stone-200/80",
                    "dark:border-stone-800",
                  )}
                >
                {hasArt ? (
                  <>
                    <img
                      src={group.bannerUrl as string}
                      alt=""
                      loading="lazy"
                      className={cn(
                        "w-full object-cover",
                        "transition-transform duration-500 ease-out group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100",
                        coverHeight,
                      )}
                    />
                    {/* Tinted from the ink, never pure black, so white type
                        keeps a 4.5:1 floor over any cover. */}
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 bg-gradient-to-t from-stone-950/85 via-stone-900/35 to-stone-900/5"
                    />
                  </>
                ) : (
                  /* No cover: paper and the topic's category glyph. Never a
                     stock photo standing in for a subject it does not show. */
                  <span
                    aria-hidden="true"
                    className={cn(
                      "block w-full bg-[linear-gradient(140deg,hsl(40_38%_97%)_0%,hsl(214_38%_96.5%)_100%)]",
                      "dark:bg-[linear-gradient(140deg,hsl(30_12%_14%)_0%,hsl(220_16%_15%)_100%)]",
                      coverHeight,
                    )}
                  />
                )}

                {!hasArt && (
                  <Icon
                    aria-hidden="true"
                    className="absolute right-2 top-2 h-4 w-4 text-stone-500/70 dark:text-stone-400/70"
                  />
                )}

                <span className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-2.5">
                  <span className="min-w-0">
                    {/* A topic name is the one thing that must be readable:
                        two lines on a phone, one clipped line once there is
                        room. */}
                    <span
                      className={cn(
                        "block font-display font-semibold leading-tight line-clamp-2 sm:truncate",
                        hasArt ? "text-white" : "text-stone-900 dark:text-stone-50",
                        singleRoom ? "text-base sm:text-lg" : "text-[13px] sm:text-sm",
                      )}
                    >
                      {label}
                    </span>
                    <span className="mt-1 flex items-center gap-1.5">
                      {/* The room is live right now — one pulsing dot per
                          card, not a badge in the corner. The ring is the
                          animation; the dot itself holds still for anyone who
                          asked for reduced motion. */}
                      <span
                        className="relative flex h-2 w-2 shrink-0"
                        aria-hidden="true"
                      >
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70 motion-reduce:hidden" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_0_2px_rgba(16,185,129,0.15)]" />
                      </span>
                      <AvatarGroup
                        activeUsers={group.users}
                        maxVisible={2}
                        size="xs"
                        className="hidden shrink-0 sm:flex"
                      />
                      <span
                        className={cn(
                          "truncate text-[11px] font-medium",
                          hasArt
                            ? "text-white/85"
                            : "text-stone-500 dark:text-stone-400",
                        )}
                      >
                        {learners}
                      </span>
                    </span>
                  </span>
                  <ArrowRight
                    className={cn(
                      "h-4 w-4 shrink-0 transition-transform duration-300 group-hover:translate-x-0.5 motion-reduce:transition-none",
                      hasArt ? "text-white/85" : "text-stone-400",
                    )}
                    aria-hidden="true"
                  />
                </span>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
});

HomeStudyingNow.displayName = "HomeStudyingNow";

export default HomeStudyingNow;
